import { useRef } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import useAuth from '@sdk/authentication/useAuth';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { DisplayLayoutModeType } from '@sdk/schedule/types/DisplayLayoutModeType';
import type { LaborUISchemaType } from '@sdk/labor/types';
import type { SortMethodType } from '@sdk/schedule/types/SortMethodType';
import useShiftAppearance from '@sdk/shiftAppearance/hooks/useShiftAppearance';

import type { GroupsArrayType } from '../types/GroupsArrayType';
import type { GroupsForDtstart } from '../types/GroupsType';
import type { RowType } from '../types/RowType';
import type { FormattedDateString } from '../types/FormattedDateString';
import type {
  AddEventType,
  OpenEventCallback,
  IsShiftLineVisibleType,
  ShouldShowPlusType,
  IsSpecialDayType,
  OpenDayOptionsType,
  GetDayTooltipType,
  HasDayOptionsType,
  OptionsFilterType,
  GoToDay,
  ToggleHeaderTabsType,
  GetDayPartGroupDisplayNameType,
  ScheduleFilters,
  EventCollectionType,
} from '../types';
import Header from '../Header';
import useEventSelection, { ToggleEventBind, SelectedEventsType } from '../hooks/useEventSelection';
import useScheduleDimensions from '../hooks/useScheduleDimensions';

import makeRows from './makeRows';
import makeGroupByDtstartRows from './makeGroupByDtstartRows';
import WindowingRenderer from './WindowingRenderer';
import ScheduleEmptyScreen from './ScheduleEmptyScreen';

type ContainerProps = {
  topOffset: number;
};
const Container = styled.div<ContainerProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-left: 20px;
  margin-right: 20px;
  height: calc(100vh - ${(props): number => props.topOffset}px);
`;

type GroupMethodByMode = {
  [mode in DisplayLayoutModeType]: SortMethodType;
};

export type Props = {
  currentPeriodDays: Array<FormattedDateString>;
  openEvent: OpenEventCallback;
  addUser: () => void;
  showAddUser: boolean;
  laborUiSchema: LaborUISchemaType;
  personaCollection: any;
  displayMode: DisplayLayoutModeType;
  displayLayoutMode: DisplayLayoutModeType;
  currentUser: AuthUserType;
  addEvent: AddEventType;
  displayWeekMode: () => void;
  selectedEvents: SelectedEventsType;
  toggleEvent: ToggleEventBind;
  isShiftLineVisible: IsShiftLineVisibleType;
  groupsArrayWithSortMethod: {
    groups: GroupsForDtstart;
    groupsArray: GroupsArrayType;
    groupMethodByMode: GroupMethodByMode;
  };
  shouldShowPlus: ShouldShowPlusType;
  isBusinessClosed: IsSpecialDayType;
  isHolidayPay: IsSpecialDayType;
  openDayOptions: OpenDayOptionsType;
  getDayOptionsTooltip: GetDayTooltipType;
  hasDayOptions: HasDayOptionsType;
  optionsFilter: OptionsFilterType;
  goToDay: GoToDay;
  toggleHeaderTabs: ToggleHeaderTabsType;
  headerStatsHidden: boolean;
  headerTabsHidden: boolean;
  sidebarHidden: boolean;
  headerFiltersHidden: boolean;
  getDayPartGroupDisplayName: GetDayPartGroupDisplayNameType;
  scheduleFilters: ScheduleFilters;
  eventCollection: EventCollectionType;
};

export const ScheduleWindowed: React.FC<React.PropsWithChildren<Props>> = ({
  currentPeriodDays,
  openEvent,
  addUser,
  showAddUser,
  laborUiSchema,
  personaCollection,
  displayMode,
  displayLayoutMode,
  addEvent,
  displayWeekMode,
  toggleEvent: toggleEventBind,
  selectedEvents,
  isShiftLineVisible,
  groupsArrayWithSortMethod,
  shouldShowPlus,
  isBusinessClosed,
  isHolidayPay,
  openDayOptions,
  getDayOptionsTooltip,
  hasDayOptions,
  optionsFilter,
  goToDay,
  toggleHeaderTabs,
  headerTabsHidden,
  headerStatsHidden,
  sidebarHidden,
  headerFiltersHidden,
  getDayPartGroupDisplayName,
  scheduleFilters,
}) => {
  if (!groupsArrayWithSortMethod) {
    return null;
  }

  /* eslint-disable react-hooks/rules-of-hooks */
  const { currentUser } = useAuth();
  const headerRef = useRef(null);
  const { groups, groupsArray, groupMethodByMode } = groupsArrayWithSortMethod;
  const { isEventSelected, hasSelectedEvents, toggleEvent } = useEventSelection({
    selectedEvents,
    toggleEventBind,
  });
  const groupMethod = groupMethodByMode[displayLayoutMode];
  const { eventNumberOfLines, shiftAppearanceDisplayMode } = useShiftAppearance({
    displayMode: displayLayoutMode,
    sortMethod: groupMethod,
  });
  const { topOffset } = useScheduleDimensions({
    headerTabsHidden,
    headerStatsHidden,
    headerFiltersHidden,
    isManager: currentUser.isManager,
  });
  /* eslint-enable react-hooks/rules-of-hooks */

  const shiftAppearanceMode = shiftAppearanceDisplayMode;

  if (displayMode !== 'week') {
    return (
      <Container topOffset={topOffset}>
        <ScheduleEmptyScreen displayWeekMode={displayWeekMode} />
      </Container>
    );
  }

  const rows = getRows({
    groupMethod,
    groupsArray,
    groups,
    showAddUser,
    personasDict: personaCollection.personasDict,
    tags: personaCollection.tags,
    scheduleFilters,
    eventNumberOfLines,
    currentPeriodDays,
    optionsFilter,
    getDayPartGroupDisplayName,
  });

  if (!rows) {
    return null;
  }

  const setScheduleScrollOffset = (offset: number): void => {
    headerRef.current?.setOffset(offset);
  };

  return (
    <Container topOffset={topOffset}>
      <Header
        ref={headerRef}
        currentPeriodDays={currentPeriodDays}
        hasNameColumn={groupMethod !== 'dtstart'}
        isBusinessClosed={isBusinessClosed}
        isHolidayPay={isHolidayPay}
        openDayOptions={openDayOptions}
        getDayOptionsTooltip={getDayOptionsTooltip}
        hasDayOptions={hasDayOptions}
        goToDay={goToDay}
      />

      <WindowingRenderer
        rows={rows}
        currentPeriodDays={currentPeriodDays}
        openEvent={openEvent}
        addUser={addUser}
        shiftAppearanceMode={shiftAppearanceMode}
        addEvent={addEvent}
        toggleEvent={toggleEvent}
        hasSelectedEvents={hasSelectedEvents}
        isEventSelected={isEventSelected}
        isShiftLineVisible={isShiftLineVisible}
        groupMethod={groupMethod}
        shouldShowPlus={shouldShowPlus}
        laborUISchema={laborUiSchema}
        isBusinessClosed={isBusinessClosed}
        isHolidayPay={isHolidayPay}
        optionsFilter={optionsFilter}
        toggleHeaderTabs={toggleHeaderTabs}
        headerStatsHidden={headerStatsHidden}
        headerTabsHidden={headerTabsHidden}
        sidebarHidden={sidebarHidden}
        headerFiltersHidden={headerFiltersHidden}
        setScheduleScrollOffset={setScheduleScrollOffset}
      />
    </Container>
  );
};

type GetRowsProps = {
  groupsArray?: GroupsArrayType;
  groups?: GroupsForDtstart;
  showAddUser: boolean;
  groupMethod: SortMethodType;
  personasDict: {
    [key in string]: any;
  };
  tags: Array<{
    id: number;
    name: string;
  }>;
  scheduleFilters: ScheduleFilters;
  eventNumberOfLines: number;
  currentPeriodDays: Array<FormattedDateString>;
  optionsFilter: OptionsFilterType;
  getDayPartGroupDisplayName: GetDayPartGroupDisplayNameType;
};
function getRows({
  groupMethod,
  groupsArray,
  groups,
  showAddUser,
  personasDict,
  tags,
  scheduleFilters,
  eventNumberOfLines,
  currentPeriodDays,
  optionsFilter,
  getDayPartGroupDisplayName,
}: GetRowsProps): Array<RowType> {
  if (groupMethod !== 'dtstart') {
    return (
      groupsArray &&
      makeRows(groupsArray, {
        groupMethod,
        showAddUser,
        personasDict,
        tags,
        scheduleFilters,
        eventNumberOfLines,
        optionsFilter,
        getDayPartGroupDisplayName,
      })
    );
  }

  if (groups) {
    return makeGroupByDtstartRows(groups, {
      scheduleFilters,
      eventNumberOfLines,
      currentPeriodDays,
      optionsFilter,
    });
  }

  return null;
}

export default {
  ReactComponent: ScheduleWindowed,
  bindings: {
    currentPeriodDays: '<',
    openEvent: '&',
    addUser: '&',
    showAddUser: '<',
    displayMode: '@',
    displayLayoutMode: '@',
    laborUiSchema: '<',
    personaCollection: '<',
    currentUser: '<',
    addEvent: '&',
    displayWeekMode: '&',
    toggleEvent: '&',
    selectedEvents: '<',
    isShiftLineVisible: '&',
    groupsArrayWithSortMethod: '<',
    shouldShowPlus: '&',
    isBusinessClosed: '&',
    isHolidayPay: '&',
    openDayOptions: '&',
    getDayOptionsTooltip: '&',
    hasDayOptions: '&',
    optionsFilter: '<',
    goToDay: '&',
    toggleHeaderTabs: '&',
    headerTabsHidden: '<',
    headerStatsHidden: '<',
    sidebarHidden: '<',
    headerFiltersHidden: '<',
    getDayPartGroupDisplayName: '&',
    scheduleFilters: '<',
    eventCollection: '<',
  },
};
